/**
 * AssessmentDraftOutput — structured Initial Assessment Report renderer
 * (Phase X.3.5).
 *
 * Composes: report header (DATE/TO/FROM/RE) + the 15 required sections in
 * source order (NEUROLOGICAL split into its 4 sub-sections; FUNCTIONAL
 * ASSESSMENT followed by the ADL/IADL table) + the closing + the
 * follow-up questions block + the billing block. Read-only; the Save +
 * Download row lives in the AssessmentDrafter page (mirrors the X.3.4.7
 * split, so this body rendering stays reusable for the drafts detail
 * page).
 *
 * Reuses EmailBillingBlock for the billing suggestion (structurally
 * identical {hours, category, rationale} shape).
 */

import { SignatureBlockLines } from './SignatureBlockLines';
import { type CSSProperties, type ReactNode, useState } from 'react';
import type {
  AdlIadlEntry,
  AssessmentDraftResponse,
  AssessmentReportSections,
  MedicalAssessmentSection,
  MedicationRow,
  ProviderRow,
  RecommendationCuration,
  RecommendationRef,
  SpicePronounChoice,
} from '@/lib/api';
import { isPickStale } from '@/lib/recommendationText';
import { Button } from '@/components/primitives/Button';
import { pronounChoiceLabel } from './AssessmentClientBlock';
import {
  sectionAnchorId,
  splitMedicalOverviewBlocks,
  type AssessmentSectionKey,
} from '@/lib/assessmentSections';
import { AdlIadlTable } from './AdlIadlTable';
import {
  AssessmentStructuredTable,
  type StructuredTableColumn,
} from './AssessmentStructuredTable';
import { AssessmentFollowUpsBlock } from './AssessmentFollowUpsBlock';
import { EmailBillingBlock } from './EmailBillingBlock';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence } from '@/lib/errorCopy';

/**
 * Paragraph trust states (2026-07 redesign). Every prose leaf is
 * 'drafted' by default (unmarked); 'edited' marks the care manager's own
 * inline edits (persistent); 'changed' marks paragraphs the last run
 * rewrote (clears when viewed). Rendered as a subtle hairline, never a
 * highlight.
 */
export type ParagraphState = 'edited' | 'changed';

/**
 * A vetted recommendation the CM clicked "Add to report" on — assembled +
 * server-resolved text, tagged with its source topic + a stable id for
 * removal. Lives in the Recommendations region of the report.
 */
export interface AssembledRecommendation {
  id: string;
  topic: string;
  text: string;
  // directory_generic closer -> show the [ADD RESOURCES HERE] fill-in marker.
  needsResources: boolean;
  // The source that lets the page re-word the text when the pronoun choice
  // changes (see useReresolvedPicks); absent on a recommendation added
  // before sources were recorded, which is never re-worded.
  ref?: RecommendationRef;
  curation?: RecommendationCuration;
  worded_with?: SpicePronounChoice;
  /** The choice key for which re-wording failed; the text is kept. */
  stale_for?: string;
}

export interface AssessmentDraftOutputProps {
  response: AssessmentDraftResponse;
  /**
   * v2 click-to-insert: vetted recommendations assembled into the report's
   * Recommendations section. Removable (the CM curates the set). Empty/absent
   * = the section is just the model's summary prose.
   */
  recommendedResources?: AssembledRecommendation[];
  onRemoveRecommendation?: (id: string) => void;
  /**
   * The pronoun choice key in effect on the page. An inserted
   * recommendation whose re-wording failed for it shows a note naming the
   * choice it is still worded with, and a Try again control
   * (onRetryRecommendation).
   */
  currentPronounKey?: string;
  onRetryRecommendation?: (id: string) => void;
  /**
   * Second-round edits: when supplied, the prose blocks become
   * click-to-edit (paragraph swaps to a textarea; blur returns to prose)
   * and every keystroke bubbles the full updated response up, so the
   * page's Save + refine + .docx paths all see the edited text. Absent =
   * the pre-existing read-only rendering (drafts detail page reuse).
   *
   * Editable surface: the section prose leaves (including the Medical
   * Assessment + Neurological sub-sections, the summary synthesis, and
   * each summary recommendation body). NOT editable inline: the report
   * header, the sacred ADL/IADL table, and the server-built closing
   * blocks; targeted changes there go through the change-request run.
   */
  onResponseChange?: (next: AssessmentDraftResponse) => void;
  /**
   * Paragraph trust states, keyed by leaf path (e.g. 'history',
   * 'medical_assessment.overview', 'summary_and_recommendations.
   * recommendations.0'). Absent/empty = every paragraph reads as drafted.
   */
  paragraphStates?: Record<string, ParagraphState>;
  /** A leaf a change-run touched was viewed (hover/focus); clears 'changed'. */
  onParagraphSeen?: (path: string) => void;
  /** A leaf was inline-edited (the page marks it 'edited', persistent). */
  onParagraphEdited?: (path: string) => void;
  /**
   * Open-gap counts per section (gapCountsBySection): sections with open
   * flags carry a small count marker on their heading. Absent = no markers.
   */
  gapCounts?: Partial<Record<AssessmentSectionKey | 'header', number>>;
  /**
   * Replaces the default read-only follow-ups roll-up (the tool page
   * passes the interactive gap interview here). undefined = default block.
   */
  followUpsSlot?: ReactNode;
  /**
   * True = the billing suggestion does NOT render here (the tool page
   * moved it to the terminal ladder as an editable field). The read-only
   * reuse keeps the default block.
   */
  hideBilling?: boolean;
  /**
   * Rendered inside the Summary and Recommendations region, after the
   * inserted recommendations: the "Add a vetted recommendation" insert
   * action (2026-07 redesign; the standalone always-open library card
   * retired). Absent on read-only reuse.
   */
  addRecommendationSlot?: ReactNode;
}

/**
 * Auto-size the edit textarea to the content being edited (drafter
 * founder polish, 2026-07-06, item 2): grow with input, cap at a
 * sensible max (the textarea scrolls internally past it). Estimated
 * from the text (newlines + a soft-wrap width guess) because the edit
 * box is a controlled swap-in, so this recomputes on every keystroke.
 */
const TEXTAREA_MIN_ROWS = 4;
const TEXTAREA_MAX_ROWS = 24;
const TEXTAREA_WRAP_COLS = 90;

export function estimateTextareaRows(value: string): number {
  let rows = 0;
  for (const line of value.split('\n')) {
    rows += Math.max(1, Math.ceil(line.length / TEXTAREA_WRAP_COLS));
  }
  // One spare row so the caret never starts against the bottom edge.
  return Math.min(Math.max(rows + 1, TEXTAREA_MIN_ROWS), TEXTAREA_MAX_ROWS);
}

/**
 * Prose content with the Medical Assessment overview's lead-in lines
 * ("Current medications:" / "Current providers:") rendered as real
 * subheadings (drafter founder polish, 2026-07-06, item 1; the .docx
 * mirrors this via medical_overview_blocks). Inline block spans, so it
 * renders inside the read-mode button and the read-only paragraph alike.
 */
function proseWithLeadInSubheadings(value: string): ReactNode {
  const blocks = splitMedicalOverviewBlocks(value);
  if (blocks.length === 1 && blocks[0]!.heading === '') return value;
  return blocks.map((b, i) =>
    b.heading !== '' ? (
      <span
        key={i}
        className={`block text-sm font-semibold ${i > 0 ? 'mt-2' : ''} mb-0.5`}
        style={SUBHEADING_STYLE}
        data-testid="assessment-overview-subheading"
      >
        {b.heading}
      </span>
    ) : (
      <span key={i} className="block">
        {b.text}
      </span>
    ),
  );
}

/**
 * Click-to-edit prose leaf. Read mode is a button styled as the paragraph
 * (keyboard accessible); edit mode is a controlled textarea that commits
 * on every keystroke and returns to read mode on blur. Empty sections are
 * intentional (gaps live in follow-ups), so the empty state renders a
 * subtle add-text affordance instead of an invisible click target.
 * Shared with the Assessment Letter output (the same editing grammar).
 */
export function EditableProse({
  value,
  label,
  onChange,
  state,
  onSeen,
  leadInSubheadings = false,
}: {
  value: string;
  label: string;
  onChange?: (next: string) => void;
  /** Paragraph trust state; undefined reads as drafted (unmarked). */
  state?: ParagraphState;
  /** Fires on hover/focus when state is 'changed' (viewing clears it). */
  onSeen?: () => void;
  /** Medical Assessment overview: style the list lead-in lines as subheadings. */
  leadInSubheadings?: boolean;
}): ReactNode {
  const [editing, setEditing] = useState(false);

  // Trust-state hairline (a hairline, not a highlight): sage = your edit
  // (persistent), warm = changed by the last run (clears when viewed).
  const hairline: CSSProperties =
    state === 'edited'
      ? { borderLeft: '2px solid var(--color-sage-400)', paddingLeft: '8px' }
      : state === 'changed'
        ? { borderLeft: '2px solid var(--color-warm-400)', paddingLeft: '8px' }
        : {};
  const seenHandlers =
    state === 'changed' && onSeen ? { onMouseEnter: onSeen, onFocus: onSeen } : {};

  if (!onChange) {
    return <Prose>{leadInSubheadings ? proseWithLeadInSubheadings(value) : value}</Prose>;
  }

  if (editing) {
    return (
      <textarea
        autoFocus
        rows={estimateTextareaRows(value)}
        className="w-full whitespace-pre-wrap font-sans text-sm leading-relaxed text-ink bg-white border border-sage-400/40 rounded-[10px] p-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        aria-label={`Edit ${label}`}
        data-testid="assessment-editable-textarea"
      />
    );
  }

  const isEmpty = value.trim().length === 0;
  return (
    <Button
      type="button"
      onClick={() => setEditing(true)}
      variant="ghost"
      size="sm"
      title="Click to edit"
      aria-label={`Edit ${label}`}
      className="w-full text-left leading-relaxed whitespace-pre-wrap rounded-[10px] -mx-1 px-1 py-0.5 transition-colors duration-[var(--dur-base)] hover:bg-sand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      style={{
        display: 'block',
        fontWeight: 400,
        fontSize: '0.875rem',
        padding: '0.125rem 0.25rem',
        ...(isEmpty ? SUBTLE_STYLE : BODY_STYLE),
        ...hairline,
      }}
      data-testid="assessment-editable-prose"
      data-paragraph-state={state ?? 'drafted'}
      {...seenHandlers}
    >
      {isEmpty
        ? '(empty. Click to add text.)'
        : leadInSubheadings
          ? proseWithLeadInSubheadings(value)
          : value}
    </Button>
  );
}

/** Small open-gap count marker on a section heading. */
function GapChip({ count }: { count: number }): ReactNode {
  if (count <= 0) return null;
  return (
    <span
      className="ml-2 inline-flex items-center rounded-pill px-1.5 py-px align-middle text-[0.6875rem] font-semibold"
      style={{ backgroundColor: '#FBF4E8', color: '#7a5a1f' }}
      data-testid="assessment-section-gap-chip"
      aria-label={`${count} open ${count === 1 ? 'gap' : 'gaps'}`}
      title={`${count} open ${count === 1 ? 'gap' : 'gaps'} to resolve below`}
    >
      {count}
    </span>
  );
}

function RemoveIcon(): ReactNode {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

const HEADING_STYLE = { color: 'var(--color-sage-800)' } as const;
const SUBHEADING_STYLE = { color: 'var(--color-sage-400)' } as const;
const BODY_STYLE = { color: 'var(--color-ink)' } as const;
const SUBTLE_STYLE = { color: 'var(--color-sage-300)' } as const;

// All 17 sections in source order. 'neurological' + 'functional_assessment'
// are special-cased in the render (sub-sections / table); the rest are
// plain prose under a forest-green underlined heading.
const SECTION_ORDER: Array<[keyof AssessmentReportSections, string]> = [
  ['history', 'HISTORY'],
  ['medical_assessment', 'MEDICAL ASSESSMENT'],
  ['neurological', 'NEUROLOGICAL'],
  ['sensory', 'SENSORY'],
  ['sleep', 'SLEEP'],
  ['pain', 'PAIN'],
  ['cardiovascular', 'CARDIOVASCULAR'],
  ['respiratory', 'RESPIRATORY'],
  ['gastroenterological', 'GASTROENTEROLOGICAL'],
  ['urinary', 'URINARY'],
  ['musculoskeletal', 'MUSCULOSKELETAL'],
  ['integumentary', 'INTEGUMENTARY'],
  ['endocrine', 'ENDOCRINE'],
  ['functional_assessment', 'FUNCTIONAL ASSESSMENT'],
  ['social_assessment', 'SOCIAL ASSESSMENT'],
  ['home_safety_assessment', 'HOME SAFETY ASSESSMENT'],
  ['summary_and_recommendations', 'SUMMARY AND RECOMMENDATIONS'],
];

const NEURO_SUBS: Array<[keyof AssessmentReportSections['neurological'], string]> = [
  ['presentation', 'Presentation'],
  ['memory_cognition', 'Memory/Cognition'],
  ['mood_behavior', 'Mood and Behavior'],
  ['neuro_medical', 'Neuro-medical'],
];

// X.3.5.2: Medical Assessment sub-sections. overview leads (no subheading),
// then the three required sub-sections always render; insurance + pharmacy
// render only when the model supplied non-empty prose (optional per v2.1).
const MED_REQUIRED_SUBS: Array<
  [keyof Pick<MedicalAssessmentSection,
    'home_care_status' | 'past_surgical_history' | 'recent_hospitalization_history'>, string]
> = [
  ['home_care_status', 'Home Care Status'],
  ['past_surgical_history', 'Past Surgical History'],
  ['recent_hospitalization_history', 'Recent Hospitalization History'],
];

const MED_OPTIONAL_SUBS: Array<[keyof Pick<MedicalAssessmentSection, 'insurance' | 'pharmacy'>, string]> = [
  ['insurance', 'Insurance'],
  ['pharmacy', 'Pharmacy'],
];

// Meds/providers tables (prompt v8): founder columns, in order. KEEP in
// sync with the .docx table regions (patch-meds-providers-tables.mjs) and
// MEDICATION_ROW_KEYS / PROVIDER_ROW_KEYS in the api schema.
const MEDICATION_COLUMNS: ReadonlyArray<StructuredTableColumn<MedicationRow>> = [
  { field: 'name', label: 'Medication' },
  { field: 'dose', label: 'Dose' },
  { field: 'frequency', label: 'Frequency' },
  { field: 'reason', label: 'Reason for Medication' },
];

const PROVIDER_COLUMNS: ReadonlyArray<StructuredTableColumn<ProviderRow>> = [
  { field: 'name', label: 'Provider' },
  { field: 'address', label: 'Address' },
  { field: 'specialty', label: 'Specialty' },
  { field: 'phone', label: 'Phone' },
];

function Prose({ children }: { children: ReactNode }): ReactNode {
  return (
    <p className="text-sm leading-relaxed whitespace-pre-wrap" style={BODY_STYLE}>
      {children}
    </p>
  );
}

export function AssessmentDraftOutput({
  response,
  recommendedResources = [],
  onRemoveRecommendation,
  currentPronounKey = '',
  onRetryRecommendation,
  onResponseChange,
  paragraphStates,
  onParagraphSeen,
  onParagraphEdited,
  gapCounts,
  followUpsSlot,
  hideBilling = false,
  addRecommendationSlot,
}: AssessmentDraftOutputProps): ReactNode {
  const contact = useSupportContact();
  const { header, sections, adl_iadl, closing, closing_courtesy, signature, signatory_missing, follow_up_questions, billing_suggestion } =
    response;
  // The e-signature block renders line by line when the draft carries it;
  // a draft saved before the block existed shows its closing text instead.
  const signed = signature !== undefined && signature.name_line.trim().length > 0;

  const editable = onResponseChange !== undefined;

  // JSX render helper (NOT a nested component type — the element type
  // stays EditableProse itself, so its editing textarea survives
  // re-renders): threads the leaf's trust state + seen/edited callbacks.
  const leaf = (
    path: string,
    value: string,
    label: string,
    onChange?: (next: string) => void,
    leadInSubheadings = false,
  ): ReactNode => (
    <EditableProse
      value={value}
      label={label}
      onChange={
        onChange
          ? (v: string) => {
              onChange(v);
              onParagraphEdited?.(path);
            }
          : undefined
      }
      state={paragraphStates?.[path]}
      onSeen={onParagraphSeen ? () => onParagraphSeen(path) : undefined}
      leadInSubheadings={leadInSubheadings}
    />
  );
  // Immutable nested patches: every edit produces a full next-response
  // object, so the page's Save payload + refine payload see the edit.
  const patchSections = (patch: Partial<AssessmentReportSections>): void => {
    onResponseChange?.({ ...response, sections: { ...sections, ...patch } });
  };
  const patchMedical = (patch: Partial<MedicalAssessmentSection>): void => {
    patchSections({ medical_assessment: { ...sections.medical_assessment, ...patch } });
  };
  // Meds/providers tables (prompt v8): cell edits patch the row in place
  // and mark the TABLE-level leaf edited (rows have no stable key).
  const medications = sections.medical_assessment.medications ?? [];
  const providers = sections.medical_assessment.providers ?? [];
  const patchMedicationRow = (index: number, patch: Partial<MedicationRow>): void => {
    patchMedical({
      medications: medications.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    });
    onParagraphEdited?.('medical_assessment.medications');
  };
  const patchProviderRow = (index: number, patch: Partial<ProviderRow>): void => {
    patchMedical({
      providers: providers.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    });
    onParagraphEdited?.('medical_assessment.providers');
  };
  const patchNeuro = (patch: Partial<AssessmentReportSections['neurological']>): void => {
    patchSections({ neurological: { ...sections.neurological, ...patch } });
  };
  const patchSynthesis = (synthesis: string): void => {
    patchSections({
      summary_and_recommendations: { ...sections.summary_and_recommendations, synthesis },
    });
  };
  const patchRecommendationBody = (index: number, bodyText: string): void => {
    patchSections({
      summary_and_recommendations: {
        ...sections.summary_and_recommendations,
        recommendations: sections.summary_and_recommendations.recommendations.map((rec, i) =>
          i === index ? { ...rec, body: bodyText } : rec,
        ),
      },
    });
  };

  return (
    <div className="space-y-4" data-testid="assessment-draft-output">
      <section
        className="bg-paper border border-sage-800/[8%] rounded-[12px] p-4 space-y-1"
        aria-label="Assessment report"
        data-testid="assessment-report-body"
      >
        {/* Header block */}
        <div
          id={sectionAnchorId('header')}
          className="space-y-0.5 text-sm pb-2 mb-2 border-b"
          style={{ borderColor: 'var(--color-sage-300)', ...BODY_STYLE }}
        >
          <p><span className="font-semibold">DATE:</span> {header.date}</p>
          <p><span className="font-semibold">TO:</span> {header.to}</p>
          <p><span className="font-semibold">FROM:</span> {header.from}</p>
          <p>
            <span className="font-semibold">RE:</span> {header.re}
            <GapChip count={gapCounts?.header ?? 0} />
          </p>
        </div>

        {SECTION_ORDER.map(([key, title]) => (
          <div key={key}>
            <h3
              id={sectionAnchorId(key)}
              className="text-sm font-semibold mt-4 mb-1"
              style={HEADING_STYLE}
            >
              <span className="underline">{title}</span>
              <GapChip count={gapCounts?.[key] ?? 0} />
            </h3>
            {key === 'medical_assessment' ? (
              <>
                {/* Item 1 (founder polish 2026-07-06): the overview's
                    "Current medications:" / "Current providers:" lead-in
                    lines render as real subheadings. LEGACY drafts only:
                    a v8 draft carries the lists as the structured tables
                    below instead, and its overview is plain prose. */}
                {leaf(
                  'medical_assessment.overview',
                  sections.medical_assessment.overview,
                  'Medical Assessment overview',
                  editable ? (v) => patchMedical({ overview: v }) : undefined,
                  true,
                )}
                {/* Meds/providers tables (prompt v8): render only when the
                    structured rows exist; a legacy draft (empty arrays)
                    keeps its in-overview bulleted lists above. */}
                {medications.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mt-2 mb-1" style={SUBHEADING_STYLE}>
                      Current Medications
                    </h4>
                    <AssessmentStructuredTable
                      rows={medications}
                      columns={MEDICATION_COLUMNS}
                      onRowChange={editable ? patchMedicationRow : undefined}
                      state={paragraphStates?.['medical_assessment.medications']}
                      onSeen={
                        onParagraphSeen
                          ? () => onParagraphSeen('medical_assessment.medications')
                          : undefined
                      }
                      label="Current medications"
                      testId="assessment-medications-table"
                    />
                  </div>
                )}
                {providers.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mt-2 mb-1" style={SUBHEADING_STYLE}>
                      Current Providers
                    </h4>
                    <AssessmentStructuredTable
                      rows={providers}
                      columns={PROVIDER_COLUMNS}
                      onRowChange={editable ? patchProviderRow : undefined}
                      state={paragraphStates?.['medical_assessment.providers']}
                      onSeen={
                        onParagraphSeen
                          ? () => onParagraphSeen('medical_assessment.providers')
                          : undefined
                      }
                      label="Current providers"
                      testId="assessment-providers-table"
                    />
                  </div>
                )}
                {MED_REQUIRED_SUBS.map(([subKey, subTitle]) => (
                  <div key={subKey}>
                    <h4 className="text-sm font-semibold mt-2" style={SUBHEADING_STYLE}>
                      {subTitle}
                    </h4>
                    {leaf(
                      `medical_assessment.${subKey}`,
                      sections.medical_assessment[subKey],
                      subTitle,
                      editable ? (v) => patchMedical({ [subKey]: v }) : undefined,
                    )}
                  </div>
                ))}
                {MED_OPTIONAL_SUBS.map(([subKey, subTitle]) => {
                  const val = sections.medical_assessment[subKey];
                  // Optional sub-sections stay hidden when empty even in
                  // edit mode (adding one is a change-request-run job).
                  if (!val || val.trim().length === 0) return null;
                  return (
                    <div key={subKey}>
                      <h4 className="text-sm font-semibold mt-2" style={SUBHEADING_STYLE}>
                        {subTitle}
                      </h4>
                      {leaf(
                        `medical_assessment.${subKey}`,
                        val,
                        subTitle,
                        editable ? (v) => patchMedical({ [subKey]: v }) : undefined,
                      )}
                    </div>
                  );
                })}
              </>
            ) : key === 'neurological' ? (
              NEURO_SUBS.map(([subKey, subTitle]) => (
                <div key={subKey}>
                  <h4 className="text-sm font-semibold mt-2" style={SUBHEADING_STYLE}>
                    {subTitle}
                  </h4>
                  {leaf(
                    `neurological.${subKey}`,
                    sections.neurological[subKey],
                    subTitle,
                    editable ? (v) => patchNeuro({ [subKey]: v }) : undefined,
                  )}
                </div>
              ))
            ) : key === 'functional_assessment' ? (
              <>
                {leaf(
                  'functional_assessment',
                  sections.functional_assessment,
                  'Functional Assessment lead-in',
                  editable ? (v) => patchSections({ functional_assessment: v }) : undefined,
                )}
                <div className="mt-2">
                  {/* Item 3 (founder polish 2026-07-06): the table is
                      editable in edit mode. Level + Notes per row; edits
                      bubble the full response up (direct-edits-win) and
                      mark the row edited-by-you. */}
                  <AdlIadlTable
                    entries={adl_iadl}
                    onEntryChange={
                      editable
                        ? (activity, patch) => {
                            onResponseChange?.({
                              ...response,
                              adl_iadl: adl_iadl.map((e: AdlIadlEntry) =>
                                e.activity === activity ? { ...e, ...patch } : e,
                              ),
                            });
                            onParagraphEdited?.(`adl_iadl.${activity}`);
                          }
                        : undefined
                    }
                    entryStates={
                      paragraphStates
                        ? Object.fromEntries(
                            adl_iadl.map((e: AdlIadlEntry) => [
                              e.activity,
                              paragraphStates[`adl_iadl.${e.activity}`],
                            ]),
                          )
                        : undefined
                    }
                    onEntrySeen={
                      onParagraphSeen
                        ? (activity) => onParagraphSeen(`adl_iadl.${activity}`)
                        : undefined
                    }
                  />
                </div>
              </>
            ) : key === 'summary_and_recommendations' ? (
              <>
                {/* Items 5/6: the clinical synthesis paragraph, then the model's
                    recommendations as a numbered list with a bold label. */}
                {leaf(
                  'summary_and_recommendations.synthesis',
                  sections.summary_and_recommendations.synthesis,
                  'Summary synthesis',
                  editable ? patchSynthesis : undefined,
                )}
                {/* Founder ruling (2026-07-06, item 5): ONE continuous
                    numbered list. The model's recommendations lead; the
                    CM-inserted vetted ones join it in insertion order and
                    read the same as the drafted ones ("one voice in the
                    final document"), matching the .docx. Provenance stays
                    internal (data-paragraph-state="inserted"); the remove
                    affordance stays quiet. */}
                {(sections.summary_and_recommendations.recommendations.length > 0 ||
                  recommendedResources.length > 0) && (
                  <ol
                    className="mt-2 list-decimal space-y-1.5 pl-5"
                    data-testid="assessment-summary-recommendations"
                  >
                    {sections.summary_and_recommendations.recommendations.map((rec, i) => (
                      <li key={i} className="text-sm leading-relaxed" style={BODY_STYLE}>
                        <span className="font-semibold">{rec.label}.</span>{' '}
                        {editable
                          ? leaf(
                              `summary_and_recommendations.recommendations.${i}`,
                              rec.body,
                              `${rec.label} recommendation`,
                              (v) => patchRecommendationBody(i, v),
                            )
                          : rec.body}
                      </li>
                    ))}
                    {recommendedResources.map((rec) => (
                      <li
                        key={rec.id}
                        className="text-sm leading-relaxed"
                        style={BODY_STYLE}
                        data-testid="assessment-recommended-resource"
                        data-paragraph-state="inserted"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="whitespace-pre-wrap">
                            <span
                              className="font-semibold"
                              data-testid="assessment-recommended-resource-topic"
                            >
                              {rec.topic}.
                            </span>{' '}
                            {rec.text}
                          </p>
                          {onRemoveRecommendation && (
                            <Button
                              type="button"
                              onClick={() => onRemoveRecommendation(rec.id)}
                              variant="ghost"
                              size="sm"
                              aria-label={`Remove ${rec.topic} recommendation`}
                              className="shrink-0"
                            >
                              <RemoveIcon />
                            </Button>
                          )}
                        </div>
                        {isPickStale(rec, currentPronounKey) && rec.worded_with !== undefined && (
                          <p className="mt-1.5 text-xs text-warm-700">
                            <span data-testid="recommendation-stale-note">
                              Worded with {pronounChoiceLabel(rec.worded_with)}. Remove and add it again.
                            </span>
                            {onRetryRecommendation && (
                              <>
                                {' '}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => onRetryRecommendation(rec.id)}
                                  className="!min-h-0 !px-1 !py-0 underline"
                                  data-testid="recommendation-stale-retry"
                                >
                                  Try again
                                </Button>
                              </>
                            )}
                          </p>
                        )}
                        {/* directory_generic: the CM must drop in specific
                            providers. Bold, yellow-highlighted fill-in marker
                            (matches the .docx highlight). */}
                        {rec.needsResources && (
                          <p className="mt-1.5" data-testid="assessment-recommended-resource-marker">
                            <span className="rounded-[3px] bg-[#FBE89A] px-1.5 py-0.5 text-sm font-bold text-ink">
                              [ADD RESOURCES HERE]
                            </span>
                          </p>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </>
            ) : (
              leaf(
                key,
                sections[key] as string,
                title,
                editable
                  ? (v) => patchSections({ [key]: v } as Partial<AssessmentReportSections>)
                  : undefined,
              )
            )}
          </div>
        ))}

        {/* The insert action (2026-07 redesign): "Add a vetted
            recommendation" opens the library right here, pre-merged with
            the client block's context; insertion is one click. */}
        {addRecommendationSlot !== undefined && <div className="mt-3">{addRecommendationSlot}</div>}

        {/* Item 7: the closing courtesy sentence, before the signature. */}
        {closing_courtesy && closing_courtesy.trim().length > 0 && (
          <p
            className="text-sm leading-relaxed whitespace-pre-wrap mt-4"
            style={BODY_STYLE}
            data-testid="assessment-closing-courtesy"
          >
            {closing_courtesy}
          </p>
        )}

        {/* Closing: the server-built signature block, one authenticated
            identity with the FROM line. The valediction, then the typed
            signature with "(electronically signed)" beside it, the name
            with credentials, and the designation, in the report's sizes. */}
        {signed ? (
          <div
            className="text-sm leading-relaxed mt-4 pt-2 border-t space-y-0.5"
            style={{ borderColor: 'var(--color-sage-300)', ...BODY_STYLE }}
            data-testid="assessment-signature"
          >
            <p className="mb-3">{closing.split('\n')[0]}</p>
            <p>
              <span className="text-lg italic" data-testid="assessment-signature-name">
                {signature.name_line}
              </span>{' '}
              <span className="text-xs italic text-ink-soft" data-testid="assessment-signature-esign">
                {signature.esign_line}
              </span>
            </p>
            <p>{signature.credentials_line}</p>
            <SignatureBlockLines block={signature.designation_line} />
          </div>
        ) : (
          <p
            className="text-sm leading-relaxed whitespace-pre-wrap mt-4 pt-2 border-t"
            style={{ borderColor: 'var(--color-sage-300)', ...BODY_STYLE }}
          >
            {closing}
          </p>
        )}

        {/* Signature-integrity P0: unresolved author identity. The document
            ends at "Respectfully submitted," with NO name; this visible
            prompt (not part of the document body, never in the .docx) tells
            the CM how to get their signature on file. */}
        {signatory_missing === true && (
          <div
            role="status"
            className="mt-2 rounded-[10px] border border-warm-200 bg-warm-50 px-3 py-2 text-sm text-warm-700"
            data-testid="assessment-signatory-missing"
          >
            {contactSentence(
              "This report has no signature because your name isn't on your team profile yet. Ask {contact_admin} to add your name on the Team page, then generate again.",
              contact,
            )}
          </div>
        )}
      </section>

      {/* Follow-up questions: the tool page passes the interactive gap
          interview via followUpsSlot; the read-only reuse (drafts detail)
          keeps the plain roll-up block. */}
      {followUpsSlot !== undefined ? (
        followUpsSlot
      ) : (
        <AssessmentFollowUpsBlock questions={follow_up_questions} />
      )}

      {/* Billing suggestion (reuses the email billing block; identical
          shape). The tool page hides this and renders the editable
          suggestion field with the terminal actions instead. */}
      {!hideBilling && <EmailBillingBlock suggestion={billing_suggestion} />}

      <p className="text-xs" style={SUBTLE_STYLE}>
        Review every section before finalizing; the clinical judgment is yours.
      </p>

      {/* The downloaded .docx stays the deliverable. With inline editing on,
          quick wording fixes happen right here (then Save); each paragraph's
          "Click to edit" hover says so. The Word
          nudge stays for the read-only reuse (drafts detail page). */}
      {!editable && (
        <p className="text-xs" style={SUBTLE_STYLE} data-testid="edit-in-word-nudge">
          To make edits, download and finalize in Word.
        </p>
      )}
    </div>
  );
}
